import { parseInstant, toLocalDate } from "../../../domain/time";
import { isMessageKey } from "../../../shared/i18n/translate";
import type { Translate } from "../../../shared/i18n/translate";
import { formatDate } from "../../../shared/ui/format";

/**
 * Names a plan by its code as the plan catalog of the operator does (`Business` for `business`;
 * C-3, UX-116). A plan without a known name, for example one the operator added later, is shown
 * with its code.
 *
 * @param code - Plan code as sent by the server.
 * @param t - Translation function.
 * @returns The name.
 */
export function planName(code: string, t: Translate): string {
  const key = `plan.${code}`;
  const name = isMessageKey(key) ? t(key) : code;

  return name;
}

/**
 * Formats the day of an instant in a time zone (`28.10.2026` instead of
 * `2026-10-28T00:00:00+00:00`; C-3, UX-116), for example the end of a billing period at the
 * payment provider. A text that is no instant falls back to its first ten characters, which
 * {@link formatDate} returns unchanged unless they form a date.
 *
 * @param iso - Instant with offset.
 * @param languageTag - BCP 47 tag.
 * @param timeZone - IANA time zone.
 * @returns The formatted day.
 */
export function formatInstantDay(iso: string, languageTag: string, timeZone: string): string {
  let day: string;

  try {
    day = toLocalDate(parseInstant(iso), timeZone);
  } catch {
    day = iso.slice(0, 10);
  }

  const formatted = formatDate(day, languageTag);

  return formatted;
}
